import { Fragment } from 'react';
import classNames from 'classnames';
import * as yup from 'yup';
import { Formik, Form, FieldArray } from 'formik';

import { Modal } from '../../../modal/Modal';
import { Button } from '../basic-ui/Button';
import { Input } from '../basic-ui/Input';
import { BUTTON_TYPES } from '../../constants';

import PlusIcon from '@crazyegginc/hatch/dist/images/icon-plus.svg?react';
import CrossIcon from '@crazyegginc/hatch/dist/images/icon-cross.svg?react';
import { Select } from '../basic-ui/Select';

const utmOptions = [
  { value: 'utm_source', label: 'UTM source' },
  { value: 'utm_medium', label: 'UTM medium' },
  { value: 'utm_term', label: 'UTM term' },
  { value: 'utm_content', label: 'UTM content' },
  { value: 'utm_campaign', label: 'UTM name' },
];

const validationSchema = (utmParams) =>
  yup.object().shape({
    params: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            k: yup.string().required(utmParams ? 'Please select a param type.' : 'Please provide a key.'),
            v: yup
              .array()
              .of(yup.string().required('Please provide a value.'))
              .min(1, 'Please provide at least one value.'),
          })
          .test('unique', 'Please provide a unique key.', function (value) {
            if (!value || !value['k']) {
              return true;
            }

            if (this.parent.filter((v) => v !== value).some((v) => v['k'] === value['k'])) {
              return this.createError({
                path: `${this.path}.k`,
              });
            }

            return true;
          }),
      )
      .min(1, 'Please provide at least one key/value pair.'),
  });

const defaultParam = () => ({ k: '', v: [''] });

export function URLParamsSegmentModal({ onCancel, onSubmit, values, utmParams }) {
  return (
    <Modal dialogClassName="!top-[150px] !translate-y-0 !w-[640px]" onDismiss={onCancel}>
      <Modal.Title className="leading-none">{utmParams ? 'Ad traffic' : 'URL parameter'}</Modal.Title>
      {utmParams ? null : <div className="text-gray-200">Enter the values for your URL parameters</div>}
      <Formik
        initialValues={{ params: values ?? [defaultParam()] }}
        validationSchema={validationSchema(utmParams)}
        onSubmit={(values) => {
          onSubmit(values.params);
        }}
      >
        {({ values, errors, touched, handleBlur, handleChange, setFieldValue }) => (
          <Form>
            <FieldArray name="params">
              {(actions) => (
                <>
                  <div className="max-h-[250px] overflow-y-auto -mr-5 pr-5 styled-scrollbar mt-[30px]">
                    {values.params.map((param, i) => (
                      <Fragment key={`param-${i}`}>
                        {i !== 0 ? <div className="font-semibold my-[15px]">AND</div> : null}
                        <div
                          className="border-2 border-gray-500 p-[15px] rounded-lg relative"
                          data-testid="param-section"
                        >
                          <div className="flex items-start">
                            {utmParams ? (
                              <div className="w-[220px] relative">
                                <Select
                                  value={values.params[i].k}
                                  label="UTM parameter"
                                  labelClassName="!text-white"
                                  size="lg"
                                  placeholder="Select"
                                  options={utmOptions.map((o) => ({
                                    ...o,
                                    disabled: values.params.some(({ k }, j) => k === o.value && i !== j),
                                  }))}
                                  onChange={(value) => setFieldValue(`params[${i}].k`, value)}
                                  renderInPortal={true}
                                  popperClassName="!z-[100000]"
                                  error={touched.params?.[i]?.k && errors.params?.[i]?.k ? errors.params?.[i].k : null}
                                />
                              </div>
                            ) : (
                              <div className="relative flex items-start group">
                                <Input
                                  label="URL parameter"
                                  size="lg"
                                  className="pr-9"
                                  placeholder="Enter key (e.g. product)"
                                  name={`params[${i}].k`}
                                  value={values.params[i].k}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.params?.[i]?.k && errors.params?.[i]?.k ? errors.params?.[i].k : null}
                                />
                              </div>
                            )}
                            <div className="h-[37px] mt-[19px] mx-2.5 flex items-center font-semibold shrink-0">is</div>
                            <FieldArray name={`params[${i}].v`}>
                              {(subactions) => (
                                <div className="flex flex-col space-y-1.25">
                                  {values.params[i].v.map((value, j) => (
                                    <div className="flex items-start group/value-row" key={`param-${i}-value-${j}`}>
                                      <div className="relative flex items-start group">
                                        <Input
                                          aria-label="parameter value"
                                          size="lg"
                                          className="pr-9 group-first/value-row:mt-[19px]"
                                          placeholder="Enter value (e.g. books)"
                                          name={`params[${i}].v[${j}]`}
                                          value={values.params[i].v[j]}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={
                                            touched.params?.[i]?.v?.[j] && errors.params?.[i]?.v?.[j]
                                              ? errors.params[i].v[j]
                                              : null
                                          }
                                        />
                                        {values.params[i].v.length > 1 && (
                                          <button
                                            type="button"
                                            className={classNames(
                                              'text-gray-200 hover:text-white p-1',
                                              'items-center justify-center shrink-0',
                                              'absolute right-3 top-2.5 duration-75 hover:transition-all group-first/value-row:mt-[19px]',
                                              'hidden group-hover:flex',
                                            )}
                                            onClick={() => subactions.remove(j)}
                                          >
                                            <CrossIcon
                                              className="h-2.5 w-2.5 fill-current"
                                              aria-label={`delete value ${j}`}
                                            />
                                          </button>
                                        )}
                                      </div>
                                      {j === values.params[i].v.length - 1 ? (
                                        <Button
                                          onClick={() => subactions.push('')}
                                          variant={BUTTON_TYPES.SECONDARY}
                                          size="lg"
                                          className="w-10 group-first/value-row:mt-[19px] ml-1.25 shrink-0 !px-0"
                                        >
                                          OR
                                        </Button>
                                      ) : (
                                        <div className="h-[37px] w-10 group-first/value-row:mt-[19px] ml-1.25 flex items-center justify-center font-semibold shrink-0">
                                          OR
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </FieldArray>
                          </div>
                          {values.params?.length > 1 && (
                            <button
                              type="button"
                              className={classNames(
                                'text-gray-200 hover:text-white p-1',
                                'items-center justify-center shrink-0',
                                'absolute right-3 top-3 flex duration-75 hover:transition-all',
                              )}
                              onClick={() => actions.remove(i)}
                            >
                              <CrossIcon className="h-2.5 w-2.5 fill-current" aria-label={`delete param ${i}`} />
                            </button>
                          )}
                        </div>
                      </Fragment>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => actions.push(defaultParam())}
                    className="flex items-center text-gray-200 hover:text-white font-semibold mt-[15px] duration-75 hover:transition-all"
                  >
                    <PlusIcon className="mr-2 h-2.5 w-2.5 fill-current" />
                    Add another parameter
                  </button>
                </>
              )}
            </FieldArray>
            <div className="flex items-center justify-between mt-[30px]">
              <Button
                size="lg"
                variant={BUTTON_TYPES.SECONDARY}
                onClick={() => setFieldValue('params', [defaultParam()])}
              >
                Clear all
              </Button>
              <div className="flex items-center space-x-2.5">
                <Modal.Cancel onClick={onCancel} />
                <Button type="submit" size="lg">
                  Apply
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
